/**
 * ctAutofill.js — merging the portal's CT metadata into what the operator typed.
 *
 * The ten CT fields on page 1 were typed by hand. The portal already holds them
 * for any case whose DICOM went through it, and `GET /render/case/:id/report-meta`
 * already serves them — the STL/OBJ tool has used that endpoint for a year.
 * Better still, the endpoint MERGES SSE's own `.sse-meta.json` sidecar
 * server-side, so one call covers both the portal's derivation and the series
 * SSE actually reconstructed.
 *
 * ── Why the merge is a pure function and not four lines in the component ────
 *
 * Because the precedence is the part that can quietly cause harm. Autofill runs
 * on a debounce against a Case ID the operator is still typing, so a reply can
 * land AFTER they have corrected a field. If the fetch wins, the correction
 * disappears and the document carries the portal's value under the operator's
 * nose. That is the same class of defect as the stale-PDF one: nothing errors,
 * the screen looks right, and the wrong number ships.
 *
 * So: **the operator always wins, and only empty fields are filled.** Stated
 * here, tested here, rather than asserted in a comment next to a setState.
 *
 * Same rule the SPE report path already applies to the case-ID prefill in
 * CaseReportBuilder — "the operator's own entry wins".
 */

import { CT_FIELD_KEYS, hasDisplayableCtData } from '../sseReport.js'

/** Is this value something the operator actually entered? */
const filled = (v) => v != null && String(v).trim() !== ''

const NO_CT = Object.freeze({})

/**
 * The CT fields held for this case.
 *
 * CT data belongs to ONE case, so it is held with the case it was filled for:
 * `{ caseKey, info }`. Fields held for another case are not this case's — and
 * because the merge keeps every filled field as the operator's own, case A's
 * fields left in place would have "kept" all of them over case B's portal
 * values, and case B's report would print case A's scan.
 *
 * There are no fields without a case: the Builder takes CT fields only once a
 * Case ID is entered. Two earlier rules for fields typed before one both went
 * wrong — adopted by the next case, they followed the operator from case to
 * case; tied to the first case looked up, they were lost to a half-typed id.
 *
 * Returns one frozen empty object, not a fresh `{}`, so a memo that depends on
 * the result does not recompute on every render.
 */
export const ctInfoFor = (held, caseKey) =>
  (held?.caseKey && held.caseKey === caseKey ? held.info ?? NO_CT : NO_CT)

/**
 * Merge fetched CT metadata under what is already there.
 *
 * @param {object} current  what the operator has typed so far
 * @param {object} fetched  `ct_info` from report-meta
 * @returns {{ctInfo: object, filledKeys: string[], keptKeys: string[]}}
 *   `filledKeys` — fields the fetch supplied
 *   `keptKeys`   — fields the fetch had a value for and the operator's entry kept
 *
 * Both lists are returned so the UI can say what happened instead of silently
 * changing the form under the operator.
 */
export function mergeCtInfo(current = {}, fetched = {}) {
  const out = { ...current }
  const filledKeys = []
  const keptKeys = []
  for (const key of CT_FIELD_KEYS) {
    const incoming = fetched?.[key]
    if (!filled(incoming)) continue
    if (filled(current?.[key])) { keptKeys.push(key); continue }
    out[key] = incoming
    filledKeys.push(key)
  }
  // Carried even though it is not one of the printed rows: `isOldScan` prefers
  // an upstream study_age_days over recomputing, so one scan cannot end up with
  // two different ages across the two documents. Not gated on being empty —
  // this is the backend's own arithmetic, not a field anyone types.
  if (Number.isFinite(Number(fetched?.study_age_days))) {
    out.study_age_days = Number(fetched.study_age_days)
  }
  return { ctInfo: out, filledKeys, keptKeys }
}

/**
 * What to tell the operator about a fetch.
 *
 * `empty` is its own state, and that distinction is the whole point. The backend
 * answers **200 with empty objects** both when a case has no DICOM in the portal
 * and when the Case ID does not exist — a success that carries nothing. Saying
 * "no DICOM for this case" when the truth is "you mistyped the id" sends someone
 * looking for a scan that is sitting right there.
 *
 * Uses `hasDisplayableCtData` — the SAME predicate the PDF uses to decide whether
 * to draw the CT block — so the status line can never promise a block the report
 * then omits.
 *
 * @returns {'ok'|'empty'|'error'}
 */
export const ctFetchStatus = (ctInfo) => (hasDisplayableCtData(ctInfo ?? {}) ? 'ok' : 'empty')

/** Operator-facing wording for each status. Drafted here, not at the keyboard. */
export function ctFetchMessage(status, { filledKeys = [], keptKeys = [] } = {}) {
  if (status === 'error') {
    return 'Could not reach the portal for this case’s CT data. Type the fields, or try again.'
  }
  if (status === 'empty') {
    return 'No CT data in the portal for this Case ID — either this case’s DICOM did not go '
      + 'through the portal, or the Case ID is not the one it was uploaded under. Type the fields.'
  }
  const parts = []
  if (filledKeys.length) parts.push(`filled ${filledKeys.length} field${filledKeys.length === 1 ? '' : 's'}`)
  if (keptKeys.length) {
    parts.push(`kept your ${keptKeys.length} edited field${keptKeys.length === 1 ? '' : 's'}`)
  }
  return `CT data loaded from the portal${parts.length ? ` — ${parts.join(', ')}` : ''}.`
}

/**
 * Should the quality waiver be suggested?
 *
 * Suggested, never set: the waiver is a statement Osteo3d makes about its own
 * work, and it is the operator's to make. Mirrors the STL/OBJ tool, which
 * derives the same suggestion from the same field.
 */
export const waiverSuggested = (ctInfo = {}) =>
  Number.isFinite(Number(ctInfo.study_age_days)) && Number(ctInfo.study_age_days) > 30
